import type { AnalysisPercentileRowDto } from '@/api/campaign-analysis-client';
import type { RankCode, RankWorkingRowDto } from '@/api/rank-admin-client';
import { SENIORITY_LEVELS_ORDERED } from '@/lib/operational-view-labels';
import type { RankCounterKey } from '@/lib/rank-labels';

// =============================================================================
// analysis-fusion.ts — Logique PURE de la page Analyse fusionnée (lot U1, ADR-FUSION-ANALYSE-RANG-01).
// =============================================================================
// La page fusionnée (distribution anonymisée + ranking nominatif sur un seul écran) DÉRIVE beaucoup :
// masquage K-anonymity, TOP N par couple, tri sur les deux écarts, chips filtrantes, compteurs de tags.
// Toute cette logique vit ICI, en fonctions PURES et testables, pour que
// le composant ne porte que du rendu (et que les UT distinctifs n'aient pas besoin du DOM).
//
// ZÉRO règle métier serveur n'est réimplémentée : les percentiles winsorisés, les écarts (`ecartP50Pct`,
// `ecartTargetPricePct`), le percentile et l'effectif du couple (`coupleEffectiveCount`) sont CALCULÉS par
// l'API (lots A1/A2) et seulement LUS ici. Ce module ne fait que composer, filtrer, trier et QUALIFIER
// (tiret « masqué » vs tiret « pas de réponse »).

// --- K-anonymity : qualification du tiret (décision GATE_HUMAN John 2026-09-17) --------------------
// « Je confirme que les tirets sont les données masquées suite au K-anonimity. » Le seuil effectif est
// max(K, 2) (DBU §2.6 : K admin-modifiable, défaut 5, plancher de représentativité 2).

/** Seuil effectif de K-anonymity : max(K, 2). `undefined` (méta non chargée) ⇒ défaut DBU 5. */
export function kAnonymityFloor(threshold: number | undefined): number {
  return Math.max(threshold ?? 5, 2);
}

/**
 * Le couple est-il MASQUÉ par la K-anonymity ? `coupleEffectiveCount` est l'effectif retenu par
 * l'analyse (A1). `null`/absent ⇒ couple SANS statistiques consolidées : rien à masquer (le tiret,
 * s'il y en a un, vient de l'absence de réponse).
 */
export function isCoupleMasked(
  coupleEffectiveCount: number | null | undefined,
  floor: number,
): boolean {
  return (
    coupleEffectiveCount !== null &&
    coupleEffectiveCount !== undefined &&
    coupleEffectiveCount < floor
  );
}

/** Nature d'un tiret affiché : masquage K-anon, absence de réponse, ou valeur présente. */
export type DashKind = 'masked' | 'absent' | 'value';

/**
 * Qualifie une métrique dérivée de la distribution (écart médiane, percentile) :
 *   • couple sous le seuil K-anon ⇒ `masked` (tiret « masqué », JAMAIS 0 €) ;
 *   • valeur `null` ⇒ `absent` (le fournisseur n'a pas répondu sur ce niveau) ;
 *   • sinon `value`.
 */
export function qualifyMetric(
  value: number | null | undefined,
  masked: boolean,
): DashKind {
  if (masked) return 'masked';
  return value === null || value === undefined ? 'absent' : 'value';
}

// --- Lignes du ranking (grain Profil × Séniorité × Fournisseur) ------------------------------------

/** Clé de binôme FOURNISSEUR × PROFIL — grain du tag de rang (G0-D1-8) et de la sélection en masse. */
export function coupleKey(supplierSiret: string, profileCode: string): string {
  return `${supplierSiret}::${profileCode}`;
}

/** Clé de couple PROFIL × SÉNIORITÉ — grain du TOP N et de la distribution. */
export function levelKey(profileCode: string, seniorityLevel: string): string {
  return `${profileCode}::${seniorityLevel}`;
}

/**
 * Une ligne du ranking PRÊTE à rendre : la ligne du contrat D1a + ce que la page en dérive (clé de
 * binôme, masquage K-anon, qualification des tirets). Aucune valeur n'est recalculée.
 */
export interface RankingViewRow {
  readonly row: RankWorkingRowDto;
  /** Clé de binôme (siret, profil) — tag de rang et sélection en masse. */
  readonly coupleKey: string;
  /** Le couple (profil × niveau) est sous le seuil K-anonymity. */
  readonly masked: boolean;
  /** Nature de la cellule « Écart méd. » (masquée / absente / valeur). */
  readonly ecartMedianeKind: DashKind;
  /** Nature de la cellule « Percentile » (masquée / absente / valeur). */
  readonly percentileKind: DashKind;
  /** Nature de la cellule « Écart prix cible » — JAMAIS masquée (le prix cible est une donnée admin). */
  readonly ecartCibleKind: DashKind;
  /**
   * Sous-famille du profil (référentiel `stacks`, A1 : `profiles[].sousFamille`). `null` si le
   * référentiel ne la porte pas — la PREMIÈRE colonne du ranking affiche alors « — » (U1-bis, retour
   * GATE_HUMAN du 2026-09-18). Portée sur la ligne de vue (et non lue par une map à chaque rendu) pour
   * que le tri ET l'export la voient sans rejointure.
   */
  readonly sousFamille: string | null;
}

/**
 * Projette les lignes du read-model en lignes de vue (masquage K-anon qualifié + sous-famille jointe).
 * `sousFamilleByProfile` vient du référentiel `stacks` ; absent ⇒ sous-famille inconnue partout.
 */
export function buildRankingRows(
  rows: readonly RankWorkingRowDto[],
  floor: number,
  sousFamilleByProfile: ReadonlyMap<string, string> = new Map<string, string>(),
): readonly RankingViewRow[] {
  return rows.map((row) => {
    const masked = isCoupleMasked(row.coupleEffectiveCount, floor);
    return {
      row,
      coupleKey: coupleKey(row.supplierSiret, row.profileCode),
      masked,
      sousFamille: sousFamilleByProfile.get(row.profileCode) ?? null,
      ecartMedianeKind: qualifyMetric(row.ecartP50Pct, masked),
      percentileKind: qualifyMetric(row.percentile, masked),
      // Le prix cible vient de la configuration de campagne (A1) : il n'est pas un agrégat de
      // distribution, donc la K-anonymity ne le masque pas.
      ecartCibleKind: qualifyMetric(row.ecartTargetPricePct, false),
    };
  });
}

// --- Filtres (état d'URL : profil · sous-famille · fournisseur · tag) -----------------------------

/**
 * Filtres de la page (tous portés par l'URL — RULE_REF_006, aucun localStorage).
 *
 * LOT F1 (GATE_HUMAN 2026-09-21, arbitrage « sélection multiple par cases à cocher ») : profil,
 * sous-famille et fournisseur sont des LISTES. Liste absente OU VIDE = aucun filtre, donc « tous » :
 * il n'existe aucune valeur sentinelle. Le tag reste un choix unique (chip du ranking, hors lot).
 */
export interface AnalysisFiltersState {
  readonly profileCodes?: readonly string[] | undefined;
  readonly sousFamilles?: readonly string[] | undefined;
  readonly supplierSirets?: readonly string[] | undefined;
  /** Chip de tag active (filtre sur le rang du binôme) ; `undefined` = tous les rangs. */
  readonly tag?: RankCode | undefined;
}

/**
 * Vrai si `candidate` satisfait le filtre `values` (OU logique À L'INTÉRIEUR d'un filtre).
 * Liste absente ou vide ⇒ le filtre n'est pas posé, tout passe. Une valeur NULLE (sous-famille
 * inconnue) ne satisfait JAMAIS un filtre posé — comportement conservé de la version à valeur unique.
 */
function matchesAnyValue(
  values: readonly string[] | undefined,
  candidate: string | null,
): boolean {
  if (values === undefined || values.length === 0) {
    return true;
  }
  return candidate !== null && values.includes(candidate);
}

/**
 * Filtre les lignes du ranking en ET logique ENTRE filtres, OU logique DANS un filtre (DBU §2.6 v2.8,
 * étendu au multi-valeurs par le lot F1). La sous-famille est LUE SUR LA LIGNE (jointe une fois par
 * `buildRankingRows`) : une seule source, celle qu'affiche la 1re colonne.
 */
export function filterRankingRows(
  rows: readonly RankingViewRow[],
  filters: AnalysisFiltersState,
): readonly RankingViewRow[] {
  return rows.filter(
    (r) =>
      matchesAnyValue(filters.profileCodes, r.row.profileCode) &&
      matchesAnyValue(filters.sousFamilles, r.sousFamille) &&
      matchesAnyValue(filters.supplierSirets, r.row.supplierSiret) &&
      (filters.tag === undefined || r.row.rank === filters.tag),
  );
}

// --- TOP N (réglage modifiable, défaut 3 — décision GATE_HUMAN « oui, TOP N est un réglage ») -----

/**
 * Valeur SENTINELLE du réglage INACTIF : aucune limitation, TOUTES les lignes du couple sont affichées.
 * Retour GATE_HUMAN du 2026-09-18, réponse « 1. Oui » : « le TOP N est toujours appliqué, or cela ne
 * devrait pas être le cas » ⇒ le réglage part INACTIF et le stepper descend explicitement jusqu'à
 * « tous ». Le paramètre d'URL `topN` n'existe donc QUE lorsque le réglage est actif.
 */
export const TOP_N_ALL = 'tous' as const;

/** Réglage TOP N : un entier de lignes par couple, ou `TOP_N_ALL` (réglage inactif). */
export type TopNSetting = number | typeof TOP_N_ALL;

/** Valeur par DÉFAUT du stepper TOP N : INACTIF (U1-bis). */
export const DEFAULT_TOP_N: TopNSetting = TOP_N_ALL;
/** Bornes du stepper ACTIF (1 = un seul retenu par couple ; 20 = plafond d'affichage raisonnable). */
export const MIN_TOP_N = 1;
export const MAX_TOP_N = 20;

/**
 * Clamp d'une valeur de TOP N venue de l'URL (arbitraire ⇒ jamais hors bornes). Absente, non finie ou
 * sous le minimum ⇒ réglage INACTIF (`TOP_N_ALL`), jamais un 1 silencieux.
 */
export function clampTopN(value: number | undefined): TopNSetting {
  if (value === undefined || !Number.isFinite(value)) return TOP_N_ALL;
  const truncated = Math.trunc(value);
  if (truncated < MIN_TOP_N) return TOP_N_ALL;
  return Math.min(MAX_TOP_N, truncated);
}

/** « − » du stepper : descend de 1, puis explicitement jusqu'à « tous » (réglage désactivé). */
export function decrementTopN(current: TopNSetting): TopNSetting {
  if (current === TOP_N_ALL) return TOP_N_ALL;
  return current <= MIN_TOP_N ? TOP_N_ALL : current - 1;
}

/** « + » du stepper : depuis « tous », ACTIVE le réglage à 1, puis 2, 3… jusqu'au plafond. */
export function incrementTopN(current: TopNSetting): TopNSetting {
  if (current === TOP_N_ALL) return MIN_TOP_N;
  return Math.min(MAX_TOP_N, current + 1);
}

/** Valeur affichée du réglage : « tous » quand il est inactif, le nombre sinon (jamais « 0 »). */
export function topNLabel(topN: TopNSetting): string {
  return topN === TOP_N_ALL ? TOP_N_ALL : String(topN);
}

/**
 * Sous-titre VRAI du ranking : il dit ce qui est réellement appliqué, y compris réglage inactif
 * (retour GATE_HUMAN 2026-09-18 : « les sous-titres … disent la vérité »).
 */
export function topNSubtitle(topN: TopNSetting): string {
  return topN === TOP_N_ALL
    ? 'toutes les lignes de chaque couple'
    : `TOP ${String(topN)} par couple`;
}

/**
 * TOP N : conserve, pour CHAQUE couple Profil × Séniorité, les N TJM retenus les plus BAS. Les lignes
 * sans TJM (aucune réponse) viennent après les lignes chiffrées : elles n'entrent dans les N que s'il
 * reste de la place. Le TOP N FILTRE l'affichage ; il n'attribue JAMAIS le rang et n'a aucun effet sur
 * la distribution anonymisée (DBU §2.6 v2.8).
 */
export function applyTopN(
  rows: readonly RankingViewRow[],
  topN: TopNSetting,
): readonly RankingViewRow[] {
  // Réglage INACTIF : aucune limitation, les lignes passent telles quelles (U1-bis).
  if (topN === TOP_N_ALL) {
    return rows;
  }
  const byLevel = new Map<string, RankingViewRow[]>();
  for (const r of rows) {
    const key = levelKey(r.row.profileCode, r.row.seniorityLevel);
    const bucket = byLevel.get(key);
    if (bucket === undefined) {
      byLevel.set(key, [r]);
    } else {
      bucket.push(r);
    }
  }
  const kept = new Set<RankingViewRow>();
  for (const bucket of byLevel.values()) {
    const ordered = [...bucket].sort((a, b) =>
      compareNullableNumber(a.row.tjmRetenuEur, b.row.tjmRetenuEur, 1),
    );
    for (const r of ordered.slice(0, topN)) {
      kept.add(r);
    }
  }
  // L'ordre d'entrée est préservé (le tri d'affichage est appliqué séparément).
  return rows.filter((r) => kept.has(r));
}

// --- Tri (ascendant / descendant sur les DEUX écarts, en plus du tri par défaut) -------------------

/** Colonne triable du ranking (décision 6 : les deux écarts sont explicitement triables). */
export type RankingSortColumn =
  | 'sousFamille'
  | 'profil'
  | 'seniorite'
  | 'tjmC1'
  | 'tjmC2'
  | 'ecartMediane'
  | 'ecartCible'
  | 'fournisseur'
  | 'siret'
  | 'rang'
  | 'percentile';

export type SortDirection = 'asc' | 'desc';

export interface RankingSort {
  readonly column: RankingSortColumn;
  readonly direction: SortDirection;
}

/** Tri par DÉFAUT (DBU §2.6 v2.8) : profil, puis TJM Cycle 2 croissant. */
export const DEFAULT_RANKING_SORT: RankingSort = {
  column: 'profil',
  direction: 'asc',
};

/** Ordre naturel des rangs (A meilleur → Non retenu). */
const RANK_SORT_INDEX: Record<RankCode, number> = {
  A: 0,
  B: 1,
  PROSPECT: 2,
  NonReferenced: 3,
};

/** Ordre naturel des niveaux de séniorité (Débutant → Référent) ; inconnu en fin. */
function seniorityIndex(level: string): number {
  const index = (SENIORITY_LEVELS_ORDERED as readonly string[]).indexOf(level);
  return index === -1 ? SENIORITY_LEVELS_ORDERED.length : index;
}

/** Compare deux nombres nullables : les `null` sont TOUJOURS renvoyés en fin de liste. */
export function compareNullableNumber(
  a: number | null | undefined,
  b: number | null | undefined,
  factor: number,
): number {
  const left = a ?? null;
  const right = b ?? null;
  if (left === null && right === null) return 0;
  if (left === null) return 1;
  if (right === null) return -1;
  return factor * (left - right);
}

/**
 * Tri d'affichage. Le tri par défaut (`profil`) applique la règle DBU « profil, puis TJM C2 croissant » ;
 * les autres colonnes trient sur leur grandeur, `null` TOUJOURS en dernier (un tiret n'est pas un zéro),
 * avec un départage stable (profil, niveau, fournisseur).
 */
export function sortRankingRows(
  rows: readonly RankingViewRow[],
  sort: RankingSort,
): readonly RankingViewRow[] {
  const factor = sort.direction === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const primary = compareByColumn(a, b, sort.column, factor);
    return primary !== 0 ? primary : tieBreak(a, b);
  });
}

function compareByColumn(
  a: RankingViewRow,
  b: RankingViewRow,
  column: RankingSortColumn,
  factor: number,
): number {
  switch (column) {
    case 'sousFamille':
      // Sous-famille inconnue (« — ») TOUJOURS en fin de liste, dans les deux sens.
      return compareNullableString(a.sousFamille, b.sousFamille, factor);
    case 'profil': {
      const byProfile = factor * a.row.profileCode.localeCompare(b.row.profileCode);
      // Tri par DÉFAUT : profil, PUIS TJM Cycle 2 croissant (le C2 gouverne le second rang).
      return byProfile !== 0
        ? byProfile
        : compareNullableNumber(a.row.responseC2Eur, b.row.responseC2Eur, factor);
    }
    case 'seniorite':
      return (
        factor * (seniorityIndex(a.row.seniorityLevel) - seniorityIndex(b.row.seniorityLevel))
      );
    case 'tjmC1':
      return compareNullableNumber(a.row.responseC1Eur, b.row.responseC1Eur, factor);
    case 'tjmC2':
      return compareNullableNumber(a.row.responseC2Eur, b.row.responseC2Eur, factor);
    case 'ecartMediane':
      // Une cellule MASQUÉE n'a pas de valeur exploitable : elle est traitée comme `null` (fin de liste).
      return compareNullableNumber(
        a.ecartMedianeKind === 'value' ? a.row.ecartP50Pct : null,
        b.ecartMedianeKind === 'value' ? b.row.ecartP50Pct : null,
        factor,
      );
    case 'ecartCible':
      return compareNullableNumber(
        a.row.ecartTargetPricePct,
        b.row.ecartTargetPricePct,
        factor,
      );
    case 'fournisseur':
      return factor * a.row.raisonSociale.localeCompare(b.row.raisonSociale);
    case 'siret':
      return factor * a.row.supplierSiret.localeCompare(b.row.supplierSiret);
    case 'rang':
      return factor * (RANK_SORT_INDEX[a.row.rank] - RANK_SORT_INDEX[b.row.rank]);
    case 'percentile':
      return compareNullableNumber(
        a.percentileKind === 'value' ? a.row.percentile : null,
        b.percentileKind === 'value' ? b.row.percentile : null,
        factor,
      );
  }
}

/** Compare deux chaînes nullables : les `null` sont TOUJOURS renvoyés en fin de liste. */
export function compareNullableString(
  a: string | null,
  b: string | null,
  factor: number,
): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return factor * a.localeCompare(b);
}

/** Départage STABLE (indépendant de la colonne triée) : profil, niveau, fournisseur. */
function tieBreak(a: RankingViewRow, b: RankingViewRow): number {
  return (
    a.row.profileCode.localeCompare(b.row.profileCode) ||
    seniorityIndex(a.row.seniorityLevel) - seniorityIndex(b.row.seniorityLevel) ||
    a.row.raisonSociale.localeCompare(b.row.raisonSociale) ||
    a.row.supplierSiret.localeCompare(b.row.supplierSiret)
  );
}

// --- Compteurs de tags (chips filtrantes) ---------------------------------------------------------

/** Un binôme (siret, profil) identifié par sa clé, avec le rang PARTAGÉ de ses lignes-niveaux. */
export interface RankingCouple {
  readonly key: string;
  readonly supplierSiret: string;
  readonly raisonSociale: string;
  readonly profileCode: string;
  readonly rank: RankCode;
}

/** Binômes DISTINCTS présents dans les lignes affichées (ordre d'apparition). */
export function deriveCouples(
  rows: readonly RankingViewRow[],
): readonly RankingCouple[] {
  const map = new Map<string, RankingCouple>();
  for (const r of rows) {
    if (!map.has(r.coupleKey)) {
      map.set(r.coupleKey, {
        key: r.coupleKey,
        supplierSiret: r.row.supplierSiret,
        raisonSociale: r.row.raisonSociale,
        profileCode: r.row.profileCode,
        rank: r.row.rank,
      });
    }
  }
  return [...map.values()];
}

/**
 * Compteurs A / B / PROSPECT / non retenus, DÉDUPLIQUÉS par binôme (1 couple = 1 unité, jamais 1 par
 * niveau) — le tag porte sur le binôme fournisseur × profil (granularité inchangée, décision 7).
 */
export function deriveTagCounters(
  rows: readonly RankingViewRow[],
): Record<RankCounterKey, number> {
  const counters: Record<RankCounterKey, number> = {
    A: 0,
    B: 0,
    PROSPECT: 0,
    nonRetenus: 0,
  };
  for (const couple of deriveCouples(rows)) {
    if (couple.rank === 'NonReferenced') {
      counters.nonRetenus += 1;
    } else {
      counters[couple.rank] += 1;
    }
  }
  return counters;
}

/** Nombre de fournisseurs DISTINCTS (SIRET) dans les lignes affichées. */
export function countDistinctSuppliers(rows: readonly RankingViewRow[]): number {
  return new Set(rows.map((r) => r.row.supplierSiret)).size;
}

// --- Distribution anonymisée GROUPÉE par profil ---------------------------------------------------

/** Une ligne-niveau du groupe de distribution (percentiles winsorisés du couple). */
export interface DistributionLevel {
  readonly row: AnalysisPercentileRowDto;
  /** Effectif du couple, lu sur le ranking (A1) : le contrat percentiles rend `count: null` si masqué. */
  readonly effectiveCount: number | null;
}

/** Un groupe de distribution = un profil + ses niveaux + la raison du masquage (une fois par groupe). */
export interface DistributionGroup {
  readonly profileCode: string;
  /** Libellé du profil (catalogue figé) ; `null` si le référentiel ne le porte pas. */
  readonly libelle: string | null;
  readonly levels: readonly DistributionLevel[];
  /** Au moins un niveau du groupe est masqué par la K-anonymity. */
  readonly hasMasked: boolean;
  /**
   * Effectif le PLUS ÉLEVÉ parmi les niveaux masqués du groupe (le cas le plus proche du seuil) —
   * alimente la mention « n répondant(s) sur K requis · percentiles masqués ». `null` si inconnu.
   */
  readonly maskedBestCount: number | null;
}

/**
 * Groupe les lignes de distribution par PROFIL (code + libellé), niveaux ordonnés Débutant → Référent.
 * L'effectif de chaque couple vient du ranking (`coupleEffectiveCount`) : le contrat percentiles ANNULE
 * `count` sur une ligne supprimée, il ne peut donc pas porter lui-même la raison du masquage.
 */
export function groupDistribution(
  rows: readonly AnalysisPercentileRowDto[],
  effectiveCountByLevel: ReadonlyMap<string, number>,
  libelleByProfile: ReadonlyMap<string, string>,
): readonly DistributionGroup[] {
  const byProfile = new Map<string, DistributionLevel[]>();
  for (const row of rows) {
    const count =
      effectiveCountByLevel.get(levelKey(row.profileCode, row.seniorityLevel)) ??
      row.count;
    const level: DistributionLevel = { row, effectiveCount: count ?? null };
    const bucket = byProfile.get(row.profileCode);
    if (bucket === undefined) {
      byProfile.set(row.profileCode, [level]);
    } else {
      bucket.push(level);
    }
  }
  return [...byProfile.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([profileCode, levels]) => {
      const ordered = [...levels].sort(
        (a, b) =>
          seniorityIndex(a.row.seniorityLevel) - seniorityIndex(b.row.seniorityLevel),
      );
      const maskedCounts = ordered
        .filter((l) => l.row.suppressed)
        .map((l) => l.effectiveCount)
        .filter((c): c is number => c !== null);
      return {
        profileCode,
        libelle: libelleByProfile.get(profileCode) ?? null,
        levels: ordered,
        hasMasked: ordered.some((l) => l.row.suppressed),
        maskedBestCount:
          maskedCounts.length === 0 ? null : Math.max(...maskedCounts),
      };
    });
}

/** Effectif par couple (profil × niveau) dérivé du ranking — source de la raison du masquage. */
export function effectiveCountByLevel(
  rows: readonly RankWorkingRowDto[],
): ReadonlyMap<string, number> {
  const map = new Map<string, number>();
  for (const row of rows) {
    const count = row.coupleEffectiveCount;
    if (count !== null && count !== undefined) {
      map.set(levelKey(row.profileCode, row.seniorityLevel), count);
    }
  }
  return map;
}

/**
 * Restreint les lignes de distribution aux couples Profil × Séniorité PRÉSENTS dans le ranking affiché.
 *
 * Retour GATE_HUMAN du 2026-09-18 (verbatim) : « La distribution affichée doit se baser sur la base des
 * éléments filtrés, à travers de la section Filtres et des filtres présents dans la section Ranking
 * Nominatif. » Le panneau « Filtre courant » (3 chiffres + bornes MIN…MAX) porte donc EXACTEMENT sur ce
 * que le ranking montre — chips Tag et réglage TOP N compris. La card « Distribution », elle, reste
 * l'agrégat anonymisé de la population complète du couple (décision 5, « Section Distribution : ok »).
 */
export function distributionForDisplayedRanking(
  distributionRows: readonly AnalysisPercentileRowDto[],
  rankingRows: readonly RankingViewRow[],
): readonly AnalysisPercentileRowDto[] {
  const displayed = new Set(
    rankingRows.map((r) => levelKey(r.row.profileCode, r.row.seniorityLevel)),
  );
  return distributionRows.filter((r) =>
    displayed.has(levelKey(r.profileCode, r.seniorityLevel)),
  );
}

/** Synthèse du filtre courant : couples affichés, couples exploitables (non masqués), fournisseurs. */
export interface CurrentFilterStats {
  readonly couples: number;
  readonly exploitables: number;
  readonly suppliers: number;
}

/** Trois chiffres du panneau « Filtre courant » (maquette 2026-09-17). */
export function currentFilterStats(
  distributionRows: readonly AnalysisPercentileRowDto[],
  rankingRows: readonly RankingViewRow[],
): CurrentFilterStats {
  return {
    couples: distributionRows.length,
    exploitables: distributionRows.filter((r) => !r.suppressed).length,
    suppliers: countDistinctSuppliers(rankingRows),
  };
}

/** Médiane entière d'un ensemble de valeurs (front-pur) ; `null` si vide. */
export function median(values: readonly number[]): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 1) {
    return sorted[mid] ?? null;
  }
  const lo = sorted[mid - 1];
  const hi = sorted[mid];
  if (lo === undefined || hi === undefined) return null;
  return Math.round((lo + hi) / 2);
}

/** Bornes de distribution du filtre courant (médiane par borne sur les couples exploitables). */
export interface DistributionBounds {
  readonly minEur: number | null;
  readonly p10Eur: number | null;
  readonly p25Eur: number | null;
  readonly p50Eur: number | null;
  readonly p75Eur: number | null;
  readonly p90Eur: number | null;
  readonly maxEur: number | null;
}

/**
 * Indicateurs P10..MAX du filtre courant (DBU §2.6 « Affichage des P10 … MAX pour le filtre courant ») :
 * MÉDIANE par borne sur les couples NON supprimés (le P50 du filtre ≠ le P50 global).
 */
export function distributionBounds(
  rows: readonly AnalysisPercentileRowDto[],
): DistributionBounds {
  const kept = rows.filter((r) => !r.suppressed);
  const pick = (get: (r: AnalysisPercentileRowDto) => number | null): number | null =>
    median(kept.map(get).filter((v): v is number => v !== null));
  return {
    minEur: pick((r) => r.minEur),
    p10Eur: pick((r) => r.p10Eur),
    p25Eur: pick((r) => r.p25Eur),
    p50Eur: pick((r) => r.p50Eur),
    p75Eur: pick((r) => r.p75Eur),
    p90Eur: pick((r) => r.p90Eur),
    maxEur: pick((r) => r.maxEur),
  };
}

/** Options du filtre « Sous-famille » présentes dans le périmètre (triées). */
export function sousFamilleOptions(
  profiles: readonly { readonly profileCode: string; readonly sousFamille?: string | null }[],
): readonly string[] {
  const seen = new Set<string>();
  for (const p of profiles) {
    const value = p.sousFamille ?? null;
    if (value !== null && value !== '') {
      seen.add(value);
    }
  }
  return [...seen].sort((a, b) => a.localeCompare(b));
}

/** Correspondance profil → sous-famille (référentiel `stacks`, A1). */
export function sousFamilleByProfile(
  profiles: readonly { readonly profileCode: string; readonly sousFamille?: string | null }[],
): ReadonlyMap<string, string> {
  const map = new Map<string, string>();
  for (const p of profiles) {
    const value = p.sousFamille ?? null;
    if (value !== null && value !== '') {
      map.set(p.profileCode, value);
    }
  }
  return map;
}

/** Fournisseurs DISTINCTS du périmètre chargé (options du filtre « Fournisseur »), triés. */
export interface SupplierOption {
  readonly supplierSiret: string;
  readonly raisonSociale: string;
}

export function supplierOptions(
  rows: readonly RankWorkingRowDto[],
): readonly SupplierOption[] {
  const map = new Map<string, SupplierOption>();
  for (const row of rows) {
    if (!map.has(row.supplierSiret)) {
      map.set(row.supplierSiret, {
        supplierSiret: row.supplierSiret,
        raisonSociale: row.raisonSociale,
      });
    }
  }
  return [...map.values()].sort(
    (a, b) =>
      a.raisonSociale.localeCompare(b.raisonSociale) ||
      a.supplierSiret.localeCompare(b.supplierSiret),
  );
}

/** Options du filtre « Profil » présentes dans le périmètre (triées). */
export function profileOptions(
  rows: readonly { readonly profileCode: string }[],
): readonly string[] {
  return [...new Set(rows.map((r) => r.profileCode))].sort((a, b) =>
    a.localeCompare(b),
  );
}

/**
 * Mention affichée sous l'en-tête : dit, une seule fois, quel périmètre porte quoi. La card
 * « Distribution » reste l'agrégat de la population complète du couple (décision 5) ; le panneau
 * « Filtre courant » suit exactement les lignes affichées du ranking (retour GATE_HUMAN 2026-09-18).
 */
export const DISTRIBUTION_UNCHANGED_NOTE =
  'la card Distribution reste l’agrégat de la population complète du couple ; le panneau « Filtre courant » suit exactement les lignes affichées du ranking';
